/**
 * PDF: "RIB ALLOTMENT PROPOSAL RATES FOR HOTELS RIVER 2026"
 *
 * Section "Reductions: Optional in Euros per person per night" — values are
 * DISCOUNT % off the base nightly rate (confirmed by Royal Belvedere):
 *   e.g. 3rd adult 20% discount → guest pays 80% of base (not 20%).
 *
 * Runtime APIs below return charge % (= 100 − reduction %) for pricing.
 */
import { BELVEDERE_SEASON_CODES } from "./belvedereContractData";

/**
 * PDF reduction table rows (lines 21–33) — discount % by season A…J.
 * FULL is synthetic (0% discount) for adult slots 1–2.
 */
export const BELVEDERE_PDF_REDUCTION_ROWS = {
  /** No discount — adult slots 1–2 (and fallbacks). */
  FULL: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  /** First Chd 2–11,99 with 2 Adults — rooms 4,5,6 */
  FIRST_CHILD_2AD_SUPERIOR: [60, 60, 60, 50, 50, 50, 50, 50, 60, 60],
  /** Chd 0–1,99 — rooms 4–14 (PDF: 100% reduction → free) */
  INFANT_0_1: [100, 100, 100, 100, 100, 100, 100, 100, 100, 100],
  /** First Child 2–11,99 with 2 Adults — rooms 7,8,9,10,11,12 (per-person breakdown) */
  FIRST_CHILD_2AD_FAMILY: [100, 100, 100, 100, 100, 100, 100, 100, 100, 100],
  /** Second Child 2–11,99 with 2 Adults — rooms 7,8,9,10,11,12 */
  SECOND_CHILD_2AD_FAMILY: [100, 100, 100, 100, 100, 100, 100, 100, 100, 100],
  /** First Chd 2–11,99 with 2 Adults — rooms 13,14 */
  FIRST_CHILD_2AD_SUITE: [80, 80, 80, 80, 80, 80, 80, 80, 80, 80],
  /** Second & Third Child 2–11,99 with 2 Adults — rooms 13,14 */
  SECOND_THIRD_CHILD_2AD_SUITE: [100, 100, 100, 100, 100, 100, 100, 100, 100, 100],
  /** 3rd Adult 12–99 — rooms 4,5,6,13,14 */
  THIRD_ADULT_SUPERIOR_SUITE: [40, 30, 30, 30, 20, 20, 20, 30, 30, 40],
  /** 3rd Adult 12–99 — rooms 8,9,10,11,12 */
  THIRD_ADULT_FAMILY: [50, 50, 50, 50, 50, 50, 50, 50, 50, 50],
  /** Third Child 2–11,99 with 2 Adults — rooms 8,12 */
  THIRD_CHILD_2AD_FAMILY: [50, 50, 50, 50, 50, 50, 50, 50, 50, 50],
  /** 4th Adult 12–99 — rooms 8,9,10,11,12 */
  FOURTH_ADULT_FAMILY: [50, 50, 50, 50, 50, 50, 50, 50, 50, 50],
  /** 4th Adult 12–99 — rooms 13,14 */
  FOURTH_ADULT_SUITE: [50, 50, 50, 50, 50, 50, 50, 50, 50, 50],
  /** 5th Adult 12–99 — room 12 */
  FIFTH_ADULT_ROOM_12: [50, 50, 50, 50, 50, 50, 50, 50, 50, 50],
  /** 5th Adult 12–99 — room 14 */
  FIFTH_ADULT_ROOM_14: [50, 50, 50, 50, 50, 50, 50, 50, 50, 50],
};

/** @deprecated Use BELVEDERE_PDF_REDUCTION_ROWS — values are discount %, not charge %. */
export const BELVEDERE_PDF_CHARGE_ROWS = BELVEDERE_PDF_REDUCTION_ROWS;

function seasonCodeToIndex(seasonCode) {
  const idx = BELVEDERE_SEASON_CODES.indexOf(seasonCode);
  return idx >= 0 ? idx : 0;
}

/** Raw PDF reduction (discount) % for a row/season. */
export function pdfReductionRowPercent(rowKey, seasonCode) {
  const row = BELVEDERE_PDF_REDUCTION_ROWS[rowKey];
  if (!Array.isArray(row) || row.length === 0) return 0;
  const pct = Number(row[seasonCodeToIndex(seasonCode)]);
  if (!Number.isFinite(pct) || pct < 0) return 0;
  return Math.min(100, pct);
}

/**
 * Charge % of base nightly rate after applying the PDF reduction.
 * 20% discount → returns 80 (guest pays 80% of base).
 */
export function pdfChargeRowPercent(rowKey, seasonCode) {
  return Math.round((100 - pdfReductionRowPercent(rowKey, seasonCode)) * 100) / 100;
}

const SUPERIOR_PP = new Set([4, 5, 6]);
const SUITE_PP = new Set([13, 14]);
const FAMILY_UNIT = new Set([7, 8, 9, 10, 12]);

export function resolvePdfAdultSlotChargePercent(roomCode, adultSlotIndex, seasonCode) {
  if (adultSlotIndex <= 2) return pdfChargeRowPercent("FULL", seasonCode);
  if (adultSlotIndex === 3) {
    if (SUPERIOR_PP.has(roomCode) || SUITE_PP.has(roomCode)) {
      return pdfChargeRowPercent("THIRD_ADULT_SUPERIOR_SUITE", seasonCode);
    }
    if (FAMILY_UNIT.has(roomCode)) {
      return pdfChargeRowPercent("THIRD_ADULT_FAMILY", seasonCode);
    }
  }
  if (adultSlotIndex === 4) {
    if (FAMILY_UNIT.has(roomCode)) return pdfChargeRowPercent("FOURTH_ADULT_FAMILY", seasonCode);
    if (SUITE_PP.has(roomCode)) return pdfChargeRowPercent("FOURTH_ADULT_SUITE", seasonCode);
  }
  if (adultSlotIndex === 5) {
    if (roomCode === 12) return pdfChargeRowPercent("FIFTH_ADULT_ROOM_12", seasonCode);
    if (roomCode === 14) return pdfChargeRowPercent("FIFTH_ADULT_ROOM_14", seasonCode);
  }
  return pdfChargeRowPercent("FULL", seasonCode);
}

export function resolvePdfInfantChargePercent(seasonCode) {
  return pdfChargeRowPercent("INFANT_0_1", seasonCode);
}

/**
 * @param {number} childSlotIndex 1-based paying child (2–11.99)
 * @param {boolean} hasTwoAdults occupancy has ≥2 adults (PDF "with 2 Adults")
 */
export function resolvePdfChildSlotChargePercent(roomCode, childSlotIndex, hasTwoAdults, seasonCode) {
  if (!hasTwoAdults) return pdfChargeRowPercent("FULL", seasonCode);

  if (SUPERIOR_PP.has(roomCode) && childSlotIndex === 1) {
    return pdfChargeRowPercent("FIRST_CHILD_2AD_SUPERIOR", seasonCode);
  }
  if (SUITE_PP.has(roomCode)) {
    if (childSlotIndex === 1) return pdfChargeRowPercent("FIRST_CHILD_2AD_SUITE", seasonCode);
    if (childSlotIndex === 2 || childSlotIndex === 3) {
      return pdfChargeRowPercent("SECOND_THIRD_CHILD_2AD_SUITE", seasonCode);
    }
  }
  if (FAMILY_UNIT.has(roomCode)) {
    if (childSlotIndex === 1) return pdfChargeRowPercent("FIRST_CHILD_2AD_FAMILY", seasonCode);
    if (childSlotIndex === 2) return pdfChargeRowPercent("SECOND_CHILD_2AD_FAMILY", seasonCode);
    if (childSlotIndex === 3 && (roomCode === 8 || roomCode === 12)) {
      return pdfChargeRowPercent("THIRD_CHILD_2AD_FAMILY", seasonCode);
    }
  }
  return pdfChargeRowPercent("FULL", seasonCode);
}

/**
 * Build charge-% slots for per-person (pp) rooms only.
 * Unit rooms use flat nightly unit rate — PDF reductions are per person and do not alter unit fare.
 */
export function buildPdfPerPersonGuestChargePercents(room, occupancy, seasonCode) {
  const childAges = Array.isArray(occupancy.childAges) ? occupancy.childAges : [];
  const infants = childAges.filter((age) => age >= 0 && age <= 1.99);
  const payingChildren = childAges.filter((age) => age > 1.99 && age <= 11.99);
  const olderAsAdults = childAges.filter((age) => age > 11.99);
  const adultSlots = (Number(occupancy.adults) || 0) + olderAsAdults.length;
  const hasTwoAdults = (Number(occupancy.adults) || 0) >= 2 && olderAsAdults.length === 0;
  const percents = [];

  for (let slot = 1; slot <= adultSlots; slot += 1) {
    percents.push(resolvePdfAdultSlotChargePercent(room.code, slot, seasonCode));
  }
  for (let i = 0; i < infants.length; i += 1) {
    percents.push(resolvePdfInfantChargePercent(seasonCode));
  }
  for (let childIndex = 1; childIndex <= payingChildren.length; childIndex += 1) {
    percents.push(
      resolvePdfChildSlotChargePercent(room.code, childIndex, hasTwoAdults, seasonCode),
    );
  }
  return percents;
}
